import { SITE_FACTS } from "@shared/siteFacts";
import { useEffect, useState } from "react";
import { useParams, Link } from "wouter";
import { getToolById, getCategoryById } from "@/lib/tools-data";
import { RELATED_TOOL_IDS } from "@/lib/related-tools";
import { selfPublishingNeighbours, SELF_PUBLISHING_LABEL } from "@/lib/self-publishing";
import { FEATURED_APP_IDS } from "@/lib/featured-apps-data";
import { Button } from "@/components/ui/button";
import { Share2, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { CreateShortcutButton } from "@/components/CreateShortcutButton";

/**
 * Sits at the bottom of every tool page. Always shows a calm baseline
 * (related tools + share + install shortcut) so every one of the 131 tools
 * gets this, including calculators with no discrete "done" moment. For the
 * ~40 tools that produce a real file download, it additionally highlights
 * itself for a few seconds right when the download fires (dispatched from
 * downloadBlob in ToolLayout.tsx) -- the moment someone has actually just
 * gotten value is the best moment to ask for a share, not before.
 */
export function ToolEngagementFooter() {
  const { id } = useParams<{ id: string }>();
  const [justCompleted, setJustCompleted] = useState(false);
  const [completedFilename, setCompletedFilename] = useState("");

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ filename?: string }>).detail;
      setCompletedFilename(detail?.filename ?? "");
      setJustCompleted(true);
      const timer = setTimeout(() => setJustCompleted(false), 8000);
      return () => clearTimeout(timer);
    };
    window.addEventListener("freedailypro:download-complete", handler);
    return () => window.removeEventListener("freedailypro:download-complete", handler);
  }, []);

  // Reset the celebratory state when navigating to a different tool.
  useEffect(() => {
    setJustCompleted(false);
  }, [id]);

  const tool = getToolById(id || "");
  const category = tool ? getCategoryById(tool.category) : undefined;
  // Premium/workflow apps show up under a different breadcrumb category than their data category
  // (e.g. Document Scanner is "Premium Applications" but lives in Image Tools), so hide the misleading block.
  const clusterTools = selfPublishingNeighbours(id || "").map(x => getToolById(x)).filter((t): t is NonNullable<typeof t> => !!t && !t.hidden);
  const curated = RELATED_TOOL_IDS[id || ""];
  const curatedTools = curated ? curated.ids.map(rid => getToolById(rid)).filter((t): t is NonNullable<typeof t> => !!t && !t.hidden) : [];
  const relatedTools = curatedTools.length > 0 ? curatedTools : clusterTools.length > 0 ? clusterTools : category && !FEATURED_APP_IDS.has(id || "") ? category.tools.filter(t => t.id !== id && !t.hidden && !FEATURED_APP_IDS.has(t.id)).slice(0, 3) : [];

  const handleShare = async () => {
    const url = `https://www.freedailypro.com/tool/${id}`;
    const shareData = {
      title: tool ? `${tool.name} - FreeDailyPro` : "FreeDailyPro",
      text: tool
        ? `I just used ${tool.name} on FreeDailyPro \u2014 it runs right in your browser.`
        : `Check out FreeDailyPro \u2014 ${SITE_FACTS.toolCountPhrase}, in your browser.`,
      url,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        // User cancelled the native share sheet -- not an error, do nothing.
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied! Share it with anyone.");
    }
  };

  if (!tool) return null;

  return (
    <div
      className={`mt-8 p-5 rounded-xl border transition-colors ${
        justCompleted ? "border-primary/30 bg-primary/5" : "border-border bg-card"
      }`}
    >
      {justCompleted && (
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="h-4 w-4 text-primary" />
          <p className="text-sm font-medium">
            {completedFilename ? `${completedFilename} is ready!` : "All done!"}
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Button size="sm" variant="outline" onClick={handleShare} className="gap-1.5">
          <Share2 className="h-3.5 w-3.5" /> Share this tool
        </Button>
        <CreateShortcutButton variant="full" />
      </div>

      {relatedTools.length > 0 && (
        <div>
          <p className="text-xs text-muted-foreground mb-2">
            {curatedTools.length > 0 && curated ? curated.label : clusterTools.length > 0 ? `${SELF_PUBLISHING_LABEL}:` : category ? `More from ${category.name}:` : "You might also like:"}
          </p>
          <div className="flex flex-wrap gap-2">
            {relatedTools.map(t => (
              <Link key={t.id} href={`/tool/${t.id}`}>
                <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-border bg-background hover:border-primary/30 transition-colors cursor-pointer group">
                  {t.name}
                  <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
